<template>
    <div class="selfinvoice-wizard-container">
        <!-- Error state -->
        <v-row v-if="errorMessage" class="ma-0" justify="center" align="center">
            <v-col cols="12" md="8" lg="6">
                <v-card class="pa-0 background-2 round-card">
                    <v-card-title class="pa-3">
                        <v-btn icon class="mr-2" @click="goBack">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <p class="pl-3 mb-0">Genera Autofattura</p>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-alert type="warning" class="ma-4">
                        {{ errorMessage }}
                    </v-alert>
                    <v-card-actions class="pa-4">
                        <v-btn outlined @click="goBack">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Torna al documento
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <!-- Wizard content -->
        <v-row v-else-if="previewData" style="height: max-content" class="ma-0 pb-offset flex-nowrap">
            <v-col
                cols="12"
                :class="['pa-1', 'wizard-column', { 'with-pdf-panel': pdfPanelExpanded }]"
            >
                <v-card class="pa-0 background-2 round-card">
                    <v-card-title class="pa-3">
                        <v-btn icon class="mr-2" @click="goBack">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <p class="pl-3 mb-0">Genera Autofattura</p>
                        <v-spacer></v-spacer>
                        <v-tooltip left>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    v-on="on"
                                    @click="togglePdfPanel"
                                    :color="pdfPanelExpanded ? 'primary' : ''"
                                >
                                    <v-icon>{{ pdfPanelExpanded ? 'mdi-chevron-right' : 'mdi-file-eye-outline' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ pdfPanelExpanded ? 'Nascondi anteprima' : 'Mostra anteprima PDF' }}</span>
                        </v-tooltip>
                    </v-card-title>
                    <v-divider></v-divider>

                    <v-card-text>
                        <!-- Riepilogo documento -->
                        <v-card outlined class="mb-4 background-1">
                            <v-card-subtitle class="font-weight-bold">
                                <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
                                Documento: {{ previewData.edocument.number }} del {{ formatDate(previewData.edocument.date) }}
                            </v-card-subtitle>
                            <v-card-text>
                                <v-chip small outlined class="mr-2">
                                    {{ previewData.edocument.type }}
                                </v-chip>
                                <span class="font-weight-bold ml-2">
                                    Totale documento: {{ formatCurrency(previewData.edocument.total) }}
                                </span>
                            </v-card-text>
                        </v-card>

                        <!-- Alert: autofattura già generata -->
                        <v-alert v-if="previewData.already_generated" type="warning" outlined class="mb-4">
                            <div>Questo documento ha già generato un'autofattura.</div>
                            <div v-if="previewData.previous_selfinvoice" class="mt-1">
                                Autofattura n. {{ previewData.previous_selfinvoice.document_number }}
                                del {{ formatDate(previewData.previous_selfinvoice.document_date) }}
                            </div>
                            <v-btn
                                v-if="previewData.previous_selfinvoice"
                                small
                                outlined
                                class="mt-2"
                                @click="goToSelfinvoice"
                            >
                                <v-icon left small>mdi-open-in-new</v-icon>
                                Vai all'autofattura
                            </v-btn>
                        </v-alert>

                        <!-- Stepper -->
                        <v-stepper v-model="step" vertical class="elevation-0" outlined>

                            <!-- STEP 1: Riepilogo Righe N6.x e Fornitore -->
                            <v-stepper-step :complete="step > 1" step="1">
                                Righe Reverse Charge e Fornitore
                                <small>{{ previewData.n6_lines.length }} righe N6.x trovate</small>
                            </v-stepper-step>
                            <v-stepper-content step="1">
                                <!-- Fornitore -->
                                <v-card outlined class="mb-4">
                                    <v-card-subtitle class="font-weight-bold">
                                        <v-icon small class="mr-2">mdi-domain</v-icon>
                                        Fornitore
                                    </v-card-subtitle>
                                    <v-card-text>
                                        <!-- Dati dal documento XML -->
                                        <v-alert type="info" text dense class="mb-4">
                                            <div class="font-weight-bold">Dati dal documento elettronico:</div>
                                            <div>{{ previewData.supplier.from_edocument.business_name }}</div>
                                            <div v-if="previewData.supplier.from_edocument.vat_number">
                                                P.IVA: {{ previewData.supplier.from_edocument.vat_number }}
                                            </div>
                                            <div v-if="previewData.supplier.from_edocument.fiscal_code">
                                                C.F.: {{ previewData.supplier.from_edocument.fiscal_code }}
                                            </div>
                                        </v-alert>

                                        <!-- Fornitore trovato -->
                                        <v-alert v-if="previewData.supplier.matched" type="success" text dense class="mb-4">
                                            <div class="font-weight-bold">Fornitore trovato in anagrafica:</div>
                                            <div>{{ previewData.supplier.matched.business_name }}</div>
                                        </v-alert>

                                        <!-- Selezione fornitore -->
                                        <v-autocomplete
                                            v-model="selectedSupplier"
                                            :items="supplierOptions"
                                            :loading="searchingSupplier"
                                            :search-input.sync="supplierSearch"
                                            item-text="business_name"
                                            item-value="id"
                                            label="Seleziona o cerca Fornitore *"
                                            placeholder="Cerca per ragione sociale, P.IVA o C.F."
                                            outlined
                                            dense
                                            return-object
                                            clearable
                                        >
                                            <template v-slot:item="{ item }">
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ item.business_name }}</v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        {{ item.vat_number || item.fiscal_code || item.subtitle }}
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </template>
                                            <template v-slot:no-data>
                                                <v-list-item>
                                                    <v-list-item-content>
                                                        <v-list-item-title>Nessun Fornitore trovato</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>

                                        <!-- Crea nuovo fornitore -->
                                        <v-btn
                                            v-if="!previewData.supplier.matched"
                                            color="primary"
                                            outlined
                                            small
                                            class="mt-2"
                                            @click="showCreateSupplierDialog = true"
                                        >
                                            <v-icon left small>mdi-plus</v-icon>
                                            Crea nuovo Fornitore
                                        </v-btn>
                                    </v-card-text>
                                </v-card>

                                <!-- Righe N6.x -->
                                <v-card outlined class="mb-4">
                                    <v-card-subtitle class="font-weight-bold">
                                        <v-icon small class="mr-2">mdi-format-list-numbered</v-icon>
                                        Righe con Natura IVA N6.x (Reverse Charge)
                                    </v-card-subtitle>
                                    <v-simple-table dense>
                                        <template v-slot:default>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Descrizione</th>
                                                    <th class="text-right">Quantità</th>
                                                    <th class="text-right">Prezzo Unit.</th>
                                                    <th class="text-right">Totale</th>
                                                    <th>Natura</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="line in previewData.n6_lines" :key="line.id">
                                                    <td>{{ line.line_number }}</td>
                                                    <td>{{ line.description }}</td>
                                                    <td class="text-right">{{ line.quantity }}</td>
                                                    <td class="text-right">{{ formatCurrency(line.unit_price) }}</td>
                                                    <td class="text-right font-weight-bold">{{ formatCurrency(line.total_price) }}</td>
                                                    <td>
                                                        <v-chip x-small outlined>{{ line.vat_nature }}</v-chip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                            <tfoot>
                                                <tr class="font-weight-bold">
                                                    <td colspan="4" class="text-right">Totale righe N6.x:</td>
                                                    <td class="text-right primary--text text-h6">{{ formatCurrency(previewData.n6_total) }}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </template>
                                    </v-simple-table>
                                </v-card>

                                <v-btn
                                    color="primary"
                                    @click="step = 2"
                                    :disabled="!selectedSupplier"
                                >
                                    Continua
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-stepper-content>

                            <!-- STEP 2: Configurazione -->
                            <v-stepper-step :complete="step > 2" step="2">
                                Configurazione Autofattura
                                <small v-if="form.subtype">{{ getFieldLabel('subtype', form.subtype) }}</small>
                            </v-stepper-step>
                            <v-stepper-content step="2">
                                <v-card outlined class="mb-4">
                                    <fields-generator
                                        v-if="fieldsset"
                                        :fieldsset.sync="fieldsset"
                                        :model.sync="form"
                                        ref="fieldsgenerator"
                                        class="pt-8 px-0"
                                        @loaded="applyDefaults"
                                    ></fields-generator>
                                </v-card>

                                <!-- Preview riga che verrà creata -->
                                <v-card outlined class="mb-4 mt-2">
                                    <v-card-subtitle class="font-weight-bold">
                                        <v-icon small class="mr-2">mdi-eye-outline</v-icon>
                                        Anteprima riga autofattura
                                    </v-card-subtitle>
                                    <v-simple-table dense>
                                        <template v-slot:default>
                                            <thead>
                                                <tr>
                                                    <th>Descrizione</th>
                                                    <th style="width: 120px">Prezzo</th>
                                                    <th style="width: 100px">Q.tà</th>
                                                    <th style="width: 180px">IVA</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <v-text-field
                                                            v-model="productNameOverride"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            class="my-1"
                                                        ></v-text-field>
                                                    </td>
                                                    <td>
                                                        <v-text-field
                                                            v-model.number="productPriceOverride"
                                                            type="number"
                                                            step="0.01"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            suffix="€"
                                                            class="my-1"
                                                        ></v-text-field>
                                                    </td>
                                                    <td>
                                                        <v-text-field
                                                            value="1"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            disabled
                                                            class="my-1"
                                                        ></v-text-field>
                                                    </td>
                                                    <td>
                                                        <v-text-field
                                                            :value="getFieldLabel('vat', form.vat)"
                                                            dense
                                                            hide-details
                                                            outlined
                                                            disabled
                                                            class="my-1"
                                                        ></v-text-field>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </v-card>

                                <v-btn text @click="step = 1" class="mr-2">
                                    <v-icon left>mdi-arrow-left</v-icon>
                                    Indietro
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    @click="step = 3"
                                    :disabled="!isConfigValid"
                                >
                                    Continua
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-stepper-content>

                            <!-- STEP 3: Conferma -->
                            <v-stepper-step step="3">
                                Conferma e Genera
                            </v-stepper-step>
                            <v-stepper-content step="3">
                                <v-card outlined class="mb-4">
                                    <v-card-subtitle class="font-weight-bold">Riepilogo</v-card-subtitle>
                                    <v-simple-table dense>
                                        <template v-slot:default>
                                            <tbody>
                                                <tr v-for="field in summaryFields" :key="field.key">
                                                    <td class="font-weight-bold" width="200">{{ field.label }}</td>
                                                    <td>{{ getSummaryValue(field) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Fornitore</td>
                                                    <td>{{ selectedSupplier ? selectedSupplier.business_name : '-' }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Riga prodotto</td>
                                                    <td>{{ productNameOverride }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Imponibile</td>
                                                    <td class="primary--text font-weight-bold text-h6">{{ formatCurrency(productPriceOverride) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Documento origine</td>
                                                    <td>
                                                        {{ previewData.edocument.type }} n. {{ previewData.edocument.number }}
                                                        del {{ formatDate(previewData.edocument.date) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </v-card>

                                <v-btn text @click="step = 2" class="mr-2">
                                    <v-icon left>mdi-arrow-left</v-icon>
                                    Indietro
                                </v-btn>
                                <v-btn
                                    color="success"
                                    :loading="generating"
                                    :disabled="generating"
                                    @click="generateSelfinvoice"
                                >
                                    <v-icon left>mdi-file-send-outline</v-icon>
                                    Genera Autofattura
                                </v-btn>
                            </v-stepper-content>
                        </v-stepper>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Colonna Anteprima PDF con slide laterale -->
            <transition name="slide-pdf">
                <v-col v-if="pdfPanelExpanded" cols="5" class="pa-1 pdf-panel-col d-none d-lg-block">
                    <v-card class="pdf-preview-card round-card background-2" outlined>
                        <v-card-title class="pa-3">
                            <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
                            <span>Anteprima Documento</span>
                            <v-spacer></v-spacer>
                            <v-btn icon small @click="downloadPdf" :disabled="!pdfData">
                                <v-icon>mdi-download</v-icon>
                            </v-btn>
                            <v-btn icon small class="ml-1" @click="togglePdfPanel">
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pa-0 background-2">
                            <v-progress-linear v-if="loadingPdf" indeterminate></v-progress-linear>
                            <object
                                v-else-if="pdfData"
                                :data="pdfData"
                                type="application/pdf"
                                class="pdf-object"
                                title="Anteprima Documento Elettronico"
                            >
                                <p class="pa-4">
                                    Il tuo browser non supporta la visualizzazione PDF inline.
                                    <a :href="pdfData" target="_blank">Clicca qui per aprire il PDF</a>
                                </p>
                            </object>
                            <v-alert v-else type="error" text class="ma-4">
                                Impossibile caricare l'anteprima PDF
                            </v-alert>
                        </v-card-text>
                    </v-card>
                </v-col>
            </transition>
        </v-row>

        <!-- Dialog crea fornitore -->
        <v-dialog v-model="showCreateSupplierDialog" max-width="800" persistent>
            <v-card class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3 mb-0">Crea nuovo Fornitore</p>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showCreateSupplierDialog = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <v-text-field
                        v-model="newSupplier.business_name"
                        label="Ragione sociale *"
                        outlined
                        dense
                        :rules="[v => !!v || 'Campo obbligatorio']"
                    ></v-text-field>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.vat_number"
                                label="Partita IVA"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.fiscal_code"
                                label="Codice Fiscale"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-text-field
                        v-model="newSupplier.legal_address_street"
                        label="Indirizzo"
                        outlined
                        dense
                    ></v-text-field>
                    <v-row>
                        <v-col cols="4">
                            <v-text-field
                                v-model="newSupplier.legal_address_zipcode"
                                label="CAP"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="5">
                            <v-text-field
                                v-model="newSupplier.legal_address_city"
                                label="Città"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="3">
                            <v-text-field
                                :value="newSupplier.province_initial || ''"
                                label="Prov."
                                outlined
                                dense
                                disabled
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.primary_telephone"
                                label="Telefono"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="newSupplier.primary_email"
                                label="Email"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" @click="showCreateSupplierDialog = false">Annulla</v-btn>
                    <v-btn text color="primary" @click="createSupplier" :loading="creatingSupplier">Crea Fornitore</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Vue from 'vue'
import FieldsGenerator from '@/components/common/FieldsGenerator'

export default {
    name: 'EdocumentSelfinvoiceWizard',

    components: {
        FieldsGenerator
    },

    props: {
        id: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            generating: false,
            errorMessage: null,
            step: 1,
            previewData: null,
            fieldsset: null,
            form: {},
            // PDF Preview
            pdfPanelExpanded: false,
            pdfData: null,
            pdfBlob: null,
            loadingPdf: false,
            // Product overrides
            productNameOverride: '',
            productPriceOverride: 0,
            // Supplier
            selectedSupplier: null,
            supplierSearch: '',
            supplierOptions: [],
            searchingSupplier: false,
            showCreateSupplierDialog: false,
            creatingSupplier: false,
            newSupplier: {},
            supplierSearchTimer: null,
        }
    },

    computed: {
        isConfigValid() {
            if (!this.fieldsset) return false
            const fields = this.fieldsset.fields || this.fieldsset
            if (!Array.isArray(fields)) return false
            return fields.every(field => {
                const isRequired = field.required || (field.validations && field.validations.includes('required'))
                if (!isRequired) return true
                const value = this.form[field.key]
                return value !== null && value !== undefined && value !== ''
            })
        },

        productName() {
            if (!this.previewData) return ''
            const edoc = this.previewData.edocument
            const supplierName = this.selectedSupplier
                ? this.selectedSupplier.business_name
                : this.previewData.supplier.from_edocument.business_name
            return 'Rif. Fattura n. ' + edoc.number + ' del ' + this.formatDate(edoc.date)
        },

        summaryFields() {
            if (!this.fieldsset) return []
            const fields = this.fieldsset.fields || this.fieldsset
            if (!Array.isArray(fields)) return []
            return fields.filter(f => this.form[f.key] !== null && this.form[f.key] !== undefined && this.form[f.key] !== '')
        }
    },

    watch: {
        supplierSearch(val) {
            if (!val || val.length < 2) return
            this.debouncedSearchSupplier(val)
        }
    },

    async mounted() {
        await this.loadPreview()
        this.$nextTick(() => {
            this.pdfPanelExpanded = true
            this.loadPdfPreview()
        })
    },

    methods: {
        async loadPreview() {
            this.errorMessage = null

            try {
                const response = await this.axios.get(`edocuments/${this.id}/selfinvoice-preview`)
                this.previewData = response.data.data
                this.initializeForm()
            } catch (error) {
                const msg = (error.response && error.response.data && error.response.data.message)
                    || 'Errore nel caricamento dei dati'
                this.$store.commit('setSnackbar', {
                    text: msg,
                    value: true,
                    color: 'warning',
                    timeout: 6000
                })
                this.goBack()
            } finally {
                this.$store.commit('endProgress')
            }
        },

        applyDefaults() {
            const fields = this.fieldsset.fields || this.fieldsset
            if (Array.isArray(fields)) {
                fields.forEach(field => {
                    if (field.default) {
                        Vue.set(this.form, field.key, field.default)
                    }
                })
            }
        },

        initializeForm() {
            // Load FieldsGenerator fields from backend
            if (this.previewData.fields) {
                this.fieldsset = this.previewData.fields
            }
            // Auto-select matched supplier
            if (this.previewData.supplier.matched) {
                this.selectedSupplier = this.previewData.supplier.matched
                this.supplierOptions = [this.previewData.supplier.matched]
            }
            // Prefill new supplier form with XML data
            const fromXml = this.previewData.supplier.from_edocument || {}
            this.newSupplier = {
                business_name: fromXml.business_name || '',
                vat_number: fromXml.vat_number || '',
                fiscal_code: fromXml.fiscal_code || '',
                legal_address_street: fromXml.legal_address_street || '',
                legal_address_zipcode: fromXml.legal_address_zipcode || '',
                legal_address_city: fromXml.legal_address_city || '',
                province_id: fromXml.province_id || null,
                province_initial: fromXml.province_initial || '',
                primary_telephone: fromXml.primary_telephone || '',
                primary_email: fromXml.primary_email || '',
            }
            // Initialize product overrides
            this.productNameOverride = this.productName
            this.productPriceOverride = this.previewData.n6_total
        },

        async generateSelfinvoice() {
            this.generating = true

            try {
                const payload = {
                    supplier_id: this.selectedSupplier.id,
                    sectional_id: this.form.sectional_id,
                    date: this.form.date,
                    subtype: this.form.subtype,
                    vat: this.form.vat,
                    layoutgroup_id: this.form.layoutgroup_id || null,
                    payment_term: this.form.payment_term || null,
                    product_name: this.productNameOverride,
                    product_price: this.productPriceOverride,
                }

                const response = await this.axios.post(`edocuments/${this.id}/generate-selfinvoice`, payload)

                this.$store.commit('setSnackbar', {
                    text: 'Autofattura generata con successo',
                    value: true,
                    color: 'success',
                    timeout: 6000
                })

                const docId = response.data.document_id
                this.$router.push(`/invoices/selfinvoices/${docId}/`)

            } catch (error) {
                const errorMessage = (error.response && error.response.data && error.response.data.message)
                    || 'Errore nella generazione dell\'autofattura'
                this.$store.commit('setSnackbar', {
                    text: errorMessage,
                    value: true,
                    color: 'error',
                    timeout: 6000
                })
            } finally {
                this.generating = false
            }
        },

        getSummaryValue(field) {
            const value = this.form[field.key]
            if (value === null || value === undefined || value === '') return '-'
            if (field.type === 'date') return this.formatDate(value)
            if (field.options) {
                const option = field.options.find(o => o.value == value)
                return option ? option.text : value
            }
            return value
        },

        getFieldLabel(fieldKey, value) {
            if (!this.fieldsset || !value) return value || ''
            const fields = this.fieldsset.fields || this.fieldsset
            if (!Array.isArray(fields)) return value
            const field = fields.find(f => f.key === fieldKey)
            if (!field || !field.options) return value
            const option = field.options.find(o => o.value == value)
            return option ? option.text : value
        },

        goBack() {
            this.$router.push(`/invoices/edocuments/${this.id}/`)
        },

        goToSelfinvoice() {
            const doc = this.previewData.previous_selfinvoice
            if (doc) {
                this.$router.push(`/invoices/selfinvoices/${doc.document_id}/`)
            }
        },

        formatDate(date) {
            if (!date) return ''
            return new Date(date).toLocaleDateString('it-IT')
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return '€ 0,00'
            return new Intl.NumberFormat('it-IT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value)
        },

        async loadPdfPreview() {
            this.loadingPdf = true
            try {
                const options = {
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Authorization': 'Bearer ' + this.$store.getters.token
                    },
                    method: 'GET'
                }
                const response = await fetch(
                    this.$store.getters.baseUrl + '/api/edocuments/e-invoice/' + this.id + '/print',
                    options
                )
                if (!response.ok) {
                    throw new Error('Errore nel caricamento del PDF')
                }
                const blob = await response.blob()
                this.pdfBlob = new Blob([blob], { type: blob.type })
                this.pdfData = window.URL.createObjectURL(this.pdfBlob)
            } catch (error) {
                console.error('Error loading PDF preview:', error)
            } finally {
                this.loadingPdf = false
            }
        },

        downloadPdf() {
            if (this.pdfBlob) {
                const link = document.createElement('a')
                const url = window.URL.createObjectURL(this.pdfBlob)
                link.href = url
                link.setAttribute('target', '_blank')
                link.download = 'Documento_' + this.id + '.pdf'
                link.click()
                setTimeout(() => {
                    window.URL.revokeObjectURL(url)
                }, 100)
            }
        },

        togglePdfPanel() {
            this.pdfPanelExpanded = !this.pdfPanelExpanded
        },

        debouncedSearchSupplier(search) {
            if (this.supplierSearchTimer) {
                clearTimeout(this.supplierSearchTimer)
            }
            this.supplierSearchTimer = setTimeout(() => {
                this.searchSuppliers(search)
            }, 300)
        },

        async searchSuppliers(search) {
            this.searchingSupplier = true
            try {
                const response = await this.axios.get('async-suppliers', {
                    params: { search: search }
                })
                this.supplierOptions = (response.data.options || []).map(opt => ({
                    id: opt.value,
                    business_name: opt.text,
                    subtitle: opt.subtitle
                }))
            } catch (error) {
                console.error('Error searching suppliers:', error)
            } finally {
                this.searchingSupplier = false
            }
        },

        async createSupplier() {
            this.creatingSupplier = true
            try {
                const response = await this.axios.post('suppliers', this.newSupplier)
                const newSupplier = {
                    id: response.data.id,
                    business_name: response.data.option.text,
                    vat_number: this.newSupplier.vat_number,
                    fiscal_code: this.newSupplier.fiscal_code
                }

                this.selectedSupplier = newSupplier
                this.supplierOptions = [newSupplier]
                this.showCreateSupplierDialog = false

                this.$store.commit('setSnackbar', {
                    text: 'Fornitore creato con successo',
                    value: true,
                    color: 'success',
                    timeout: 6000
                })
            } catch (error) {
                console.error('Error creating supplier:', error)
                this.$store.commit('setSnackbar', {
                    text: 'Errore nella creazione del Fornitore',
                    value: true,
                    color: 'error',
                    timeout: 6000
                })
            } finally {
                this.creatingSupplier = false
            }
        }
    },

    beforeDestroy() {
        if (this.pdfData) {
            window.URL.revokeObjectURL(this.pdfData)
        }
    }
}
</script>

<style scoped>
.selfinvoice-wizard-container {
    width: 100%;
}

.pb-offset {
    padding-bottom: 80px;
}

.wizard-column {
    transition: all 0.3s ease;
}

.wizard-column.with-pdf-panel {
    max-width: calc(100% - 41.666667%);
}

@media (max-width: 1263px) {
    .wizard-column.with-pdf-panel {
        max-width: 100%;
    }
}

.pdf-panel-col {
    overflow: hidden;
}

.pdf-preview-card {
    position: sticky;
    top: 80px;
}

.pdf-object {
    width: 100%;
    height: calc(100vh - 200px);
    min-height: 600px;
}

.slide-pdf-enter-active,
.slide-pdf-leave-active {
    transition: all 0.3s ease;
}

.slide-pdf-enter,
.slide-pdf-leave-to {
    transform: translateX(100%);
    opacity: 0;
    max-width: 0;
    padding: 0 !important;
}

@media (max-width: 1263px) {
    .pdf-preview-card {
        position: relative;
        top: 0;
    }

    .pdf-object {
        height: 500px;
        min-height: 400px;
    }
}
</style>
